import { drawFromUnit, isUsableRange, resolveVariables, variableDomain, type RangedVariable } from '@agavi/blocks';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefHtml } from './answer-ref-context';
import { remapInkColors } from './ink-colors';
import { processReactionHTML } from './reaction-formula-extension';

import type { TaskVariable } from '@/types/experience';

import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

// ── HTML utilities ─────────────────────────────────────────────────────────

export function htmlHasVariableChips(html: string): boolean {
  return html.includes('data-variable-chip');
}

/**
 * Every distinct variable name referenced by chips in a piece of question HTML, in document order.
 *
 * `htmlHasVariableChips` only answers whether any exist, which is all the branching toggle's
 * visibility needed. Seeding the branch equation needs the name itself: if the question mentions
 * exactly one variable, that is overwhelmingly the thing the teacher means to branch on.
 */
export function variableChipNames(html: string): string[] {
  if (!html || !htmlHasVariableChips(html)) return [];
  const div = document.createElement('div');
  div.innerHTML = html;
  const names = [...div.querySelectorAll<HTMLElement>('[data-variable-chip]')]
    .map(span => span.getAttribute('data-variable-chip') ?? '')
    .filter(Boolean);
  return [...new Set(names)];
}

// Only ever 'standard' or 'scientific' (see fmtValue below) — two fixed formatter instances,
// constructed lazily on first use and reused forever after. `Intl.NumberFormat` construction is
// genuinely expensive (locale/ICU data resolution), not the `.format()` call itself, and engines
// vary widely in how expensive — Firefox's SpiderMonkey is markedly slower to construct one than
// V8. `checkOptionChipCollisions`'s brute-force sampling can call this hundreds to thousands of
// times in a single render (once per sampled variable combination), so constructing a fresh
// instance per call (as this used to do) turned that loop into the dominant cost of every
// keystroke-triggered re-render anywhere in a task with wide-range chip variables — confirmed
// live via a Firefox profiler trace (fmtValue's own Intl.NumberFormat construction was ~79% of
// total render time), not anything about Ordering's math despite that being the first suspect.
const numberFormatters: Partial<Record<Intl.NumberFormatOptions['notation'] & string, Intl.NumberFormat>> = {};
function getNumberFormatter(notation: 'standard' | 'scientific'): Intl.NumberFormat {
  return numberFormatters[notation] ??= new Intl.NumberFormat(undefined, { notation, maximumSignificantDigits: 6 });
}

/** Exported for `checkOptionChipCollisions` (formula-plaintext.ts) — brute-force sampling needs to
 * format each sampled value exactly the way a real roll would, since two options only genuinely
 * collide if what a STUDENT would actually see matches, not just the raw numbers. */
export function fmtValue(n: number): string {
  const abs = Math.abs(n);
  const notation: 'standard' | 'scientific' =
    n !== 0 && (abs < 1e-5 || abs >= 1e5) ? 'scientific' : 'standard';
  return getNumberFormatter(notation).format(n).replace(/E\+?/, 'e');
}

/**
 * The formatter for a number the RESOLVER has already rounded — which must not round it again.
 *
 * `fmtValue` caps at six significant digits, which is the same hygiene `resolveVariables` applies
 * by default, so for years the two agreed by coincidence. A per-equation `round(x, 4)` broke the
 * coincidence: 310/3 at four decimals resolves to 103.3333 and propagates as 103.3333, while a
 * six-digit formatter showed 103.333. Displayed stopped equalling propagated — the one error this
 * whole design exists to prevent, reintroduced by the formatter rather than the resolver.
 *
 * Fifteen digits because that is where a double is still exact: enough to show anything the
 * resolver can produce (five integer digits plus ten decimals), and not so many that float noise
 * surfaces.
 */
export function fmtResolved(n: number): string {
  const abs = Math.abs(n);
  const notation: 'standard' | 'scientific' =
    n !== 0 && (abs < 1e-5 || abs >= 1e5) ? 'scientific' : 'standard';
  return (resolvedFormatters[notation] ??= new Intl.NumberFormat(undefined, { notation, maximumSignificantDigits: 15 }))
    .format(n).replace(/E\+?/, 'e');
}
const resolvedFormatters: Partial<Record<'standard' | 'scientific', Intl.NumberFormat>> = {};

/** Can this variable actually produce a range of values? Named, fully specified, and describing a
 * real interval with a usable step. Anything failing this is inert: it never rolls, so a formula
 * mentioning it is a constant dressed as a variable, and offering a Reroll for it is a button that
 * visibly does nothing.
 *
 * One definition, because it was eighteen. Every block that needed "is there anything to roll"
 * inlined this same predicate — some as `.some(...)`, some as `.filter(...)` — and
 * `rollVariableValues` below carried it a nineteenth time as its own skip-guard, negated. The two
 * must agree by construction: the set of variables Reroll claims to roll has to be exactly the set
 * it does roll. */
export type RollableVariable = TaskVariable & { min: number; max: number; step: number };

export function isRollableVariable(v: TaskVariable): v is RollableVariable {
  return !!v.name && isUsableRange(v);
}

/**
 * THE OTHER HALF of the question above, which it has been answering by accident.
 *
 * `isRollableVariable` conflates "does this have a DOMAIN to sweep" with "does this produce a
 * VALUE". For a task of randoms those are the same set, which is why one predicate served. They
 * come apart the moment a constant or an equation exists: both produce a value, neither has a
 * domain, and asking the rollable question about them answers no — which would make them
 * invisible to Reroll, to `hasRollableVariables`, to `rollVariableValues` and to the panel's
 * sample column all at once.
 *
 * So: `isRollableVariable` keeps its exact meaning and stays the one `forEachRoll` and Reroll
 * ask, because sweeping is about domains. `resolvesToValue` is for anything that needs the set a
 * student will actually see a number for.
 */
export function resolvesToValue(v: TaskVariable): boolean {
  if (!v.name) return false;
  if (v.formula?.trim()) return true;
  if (v.value != null && !isNaN(v.value)) return true;
  return isRollableVariable(v);
}

export function hasRollableVariables(variables: TaskVariable[]): boolean {
  return variables.some(isRollableVariable);
}

/**
 * One fresh draw of every variable — the teacher's Reroll, and what every block's preview reads.
 *
 * The graph work lives in `resolveVariables` in `@agavi/blocks`, shared with the student runtime's
 * `computeVariableValues`: which kind a variable is, what order equations compute in, what rounding
 * applies, and what happens to a cycle are all decided there, once. This function supplies the only
 * thing that legitimately differs — an UNSEEDED draw, because a teacher pressing Reroll wants a new
 * number, where a student's attempt must reproduce from its stored seed.
 *
 * Before, the two were independent implementations of the same idea and had already drifted: the
 * seeded one rounded where this one floored, giving students a different distribution from the one
 * their teacher previewed.
 */
export function rollVariableValues(variables: TaskVariable[]): Record<string, string> {
  return resolveVariables(variables, { format: fmtResolved, sampleRandom: drawRandomValue });
}

/**
 * The same roll, held still — every random at the first value of its own domain.
 *
 * `probeRoll` in `@agavi/blocks` answers a narrower question ("can this expression resolve at
 * all?") and omits an EQUATION variable outright, because an equation has no domain to enumerate.
 * That is right for judging whether a formula is broken and wrong for anything that has to draw a
 * number: a DataViz cell naming a derived variable found no value in the roll and had its point
 * DROPPED, so a chart standing on derived variables quietly lost its curve — and with every point
 * gone the series stopped counting as playable, which took the sonification player off the card
 * with it. Nothing warned, because the variable does exist; only its domain is unenumerable.
 *
 * Going through `resolveVariables` instead means equations are computed from the probed seeds the
 * same way they are computed from a real roll, by the one resolver, in dependency order.
 *
 * Deterministic, so an idle card does not redraw its curve on every render — the property
 * `probeRoll` was chosen for, and the only one that has to be kept.
 */
export function probeVariableValues(variables: TaskVariable[]): Record<string, string> {
  return resolveVariables(variables, {
    format: fmtResolved,
    sampleRandom: (v) => variableDomain(v)[0] ?? NaN,
  });
}

/** One unseeded draw from a random's own interval. Exported because the variables panel holds its
 * draws itself — its sample strip is STICKY until the teacher rerolls, so it cannot let
 * `rollVariableValues` draw afresh on every render — and two places drawing from the same interval
 * by different arithmetic is how a preview and a panel end up disagreeing about what a variable
 * can be. The arithmetic itself, skew included, is `drawFromUnit`'s; this supplies only the
 * unseeded uniform number that the student's seeded path supplies from its own generator. */
export function drawRandomValue(v: RangedVariable): number {
  return drawFromUnit(v, Math.random());
}


/**
 * WHAT A DISPLAY CHIP NEEDS TO KNOW ABOUT SCOPE — the names, and the word for them.
 *
 * One object rather than two parameters, because either on its own is a bug: a set with no
 * label marks the chip silently, and a label with no set marks nothing. Built by
 * `useChipScope`, which is the only thing that should construct one.
 */
export interface ChipScope {
  /** The names belonging to THIS task alone. See `LocalVariableContext`. */
  localNames: ReadonlySet<string>;
  /** What a screen reader says in place of the dot, which it cannot hear. Translated. */
  localLabel: string;
}

/** Builds the chip, marked when the name is this task's own. The one place the `·` is decided. */
function displayChip(name: string, scope?: ChipScope): HTMLElement {
  const chip = document.createElement('span');
  chip.setAttribute('data-variable-chip-display', name);
  const local = scope?.localNames.has(name) ?? false;
  chip.setAttribute('class', local ? 'ws-var-chip ws-var-chip-local' : 'ws-var-chip');
  /*
   * THE DOT IS CSS AND THE WORD IS TEXT, which is the split the References dot already makes:
   * the label carries the state, because the dot beside it cannot be heard.
   *
   * A literal `·` in `textContent` would be copied with the sentence and announced as "bullet"
   * before every local variable. `::before` cannot be copied or spoken, so the `sr-only` span
   * says it in words instead. It is inside the chip rather than an `aria-label` on it, because
   * a bare `span` has no role and an `aria-label` there is not reliably announced.
   */
  if (local) {
    const sr = document.createElement('span');
    sr.setAttribute('class', 'sr-only');
    sr.textContent = `${scope!.localLabel} `;
    chip.appendChild(sr);
  }
  chip.appendChild(document.createTextNode(`{${name}}`));
  return chip;
}

/**
 * Renders variable chip spans as styled {name} pills for normal (non-preview) display.
 *
 * `scope` marks the ones belonging to this task alone. Omitting it marks nothing, which is
 * correct for a surface with no task — and is why `invariants.test.ts` counts the call sites
 * that should have one, since a forgotten `scope` is silently unmarked rather than wrong.
 */
export function renderChipsForDisplay(html: string, scope?: ChipScope): string {
  if (!html || !htmlHasVariableChips(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-variable-chip]').forEach(span => {
    const name = span.getAttribute('data-variable-chip') ?? '';
    if (name) span.replaceWith(displayChip(name, scope));
  });
  return div.innerHTML;
}

/** Replaces variable chip spans with plain `{name}` text nodes, in place — everywhere else in the
 * app that isn't the display card should show this, never a styled chip (that's
 * `renderChipsForDisplay`'s job) and never the chip's bare-name static serialization (no
 * brackets at all — the raw HTML's own text content omits them, see variable-chip-extension.tsx's
 * `renderHTML`). Unlike `stripHtmlForLabel`, this does NOT also strip surrounding tags/formatting
 * — it's for contexts that want to keep rich formatting (bold/italic/color) intact around the
 * bracketed name, e.g. a feedback trigger-picker's option preview, where a teacher may be relying
 * on formatting to tell otherwise-identical-text options apart. `stripHtmlForLabel` composes this
 * with a full tag-strip for contexts that want a flat plain-text label instead. */
export function bracketizeVariableChips(html: string): string {
  if (!html || !htmlHasVariableChips(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-variable-chip]').forEach(span => {
    span.replaceWith(document.createTextNode(`{${span.getAttribute('data-variable-chip') ?? ''}}`));
  });
  return div.innerHTML;
}

/**
 * THE SAME THING FOR A FORMULA, which needs the RECALLS bracketised too.
 *
 * `bracketizeVariableChips` is the LABEL path: it exists so a variable reads as `{name}` wherever
 * a chip cannot be drawn, and it deliberately leaves a recall chip alone so a feedback title
 * reads "You said Chinchilla" rather than "[ara_3ba6…]". The equations toggle is a different
 * question — it is turning authored prose into a FORMULA, and a formula stores a recall as its
 * token. Going through the label path dropped the chip and left the fallback text behind, so a
 * recall survived the toggle as a word.
 *
 * Only the id is written out: the chip extension parses `data-answer-ref-alias` and derives
 * everything else, so `formulaToRichText` can rebuild a live chip from the token alone.
 */
export function bracketizeChipsForFormula(
  html: string,
  /**
   * The aliases a chip may become a TOKEN for — the ones that resolve to a number.
   *
   * A formula does arithmetic, and `FormulaField` will only draw a chip for a number facet: a
   * token for anything else is an opaque `[ara_<uuid>]` sitting in the field, unreadable and
   * undeletable as a unit. Reported exactly that way — "when i switch an mcq/dropdown to 'show
   * a calculated value', the chips become [ara_234kljsdkfj...]".
   *
   * A chip left alone instead strips to the words it last rendered as, which makes the option
   * fail the arithmetic test above it and stay blank rather than carrying a dead reference.
   */
  numericAliasIds: ReadonlySet<string>,
): string {
  if (!html) return html;
  const div = document.createElement('div');
  div.innerHTML = bracketizeVariableChips(html);
  div.querySelectorAll<HTMLElement>('[data-answer-ref-alias]').forEach(span => {
    const id = span.getAttribute('data-answer-ref-alias') ?? '';
    /* WIPED, not flattened to the words it last rendered as — the house rule for content a
       field does not take. Those words would be an undefined symbol sitting in the formula,
       which is a second way to say the same no. */
    if (numericAliasIds.has(id)) span.replaceWith(document.createTextNode(`[${id}]`));
    else span.remove();
  });
  return div.innerHTML;
}

/** Whether a piece of prose quotes an earlier answer — the chip's own attribute. */
export function htmlHasRecallChips(html: string): boolean {
  return html.includes('data-answer-ref-alias');
}

/** Every alias a piece of prose quotes, in document order. */
export function recallAliasIdsIn(html: string): string[] {
  if (!htmlHasRecallChips(html)) return [];
  const div = document.createElement('div');
  div.innerHTML = html;
  return [...div.querySelectorAll<HTMLElement>('[data-answer-ref-alias]')]
    .map(el => el.getAttribute('data-answer-ref-alias') ?? '')
    .filter(Boolean);
}

/** The opposite of `bracketizeVariableChips` — rebuilds real, interactive chip markup from a
 * plain string's own `{name}` syntax (the same bracket convention a formula field's chips already
 * use). Used when equations-mode content needs to become real rich-text again — e.g. turning
 * "Treat options/items as equations" off copies each option/item's current formula into its text
 * field, and a bare `{name}` substring there should become a genuine, re-editable chip (insertable
 * back into the shared toolbar's picker, correctly detected by `htmlHasVariableChips`), not literal
 * curly braces sitting in plain text. Wraps the result in a `<p>`, matching every rich-text field's
 * own top-level wrapper. Only `{`/`}`-delimited names are special-cased; everything else is
 * escaped as plain text, since a formula string is never itself real markup. */
export function formulaToRichText(
  formula: string, knownNames: Set<string>,
  /**
   * The aliases a `[ara_…]` token is allowed to become a chip for.
   *
   * Same rule as `knownNames` one line down and for the same reason: an id naming no alias is a
   * token pointing at nothing, and turning it into a chip through the toggle would smuggle in a
   * reference that never went through the recall picker. Omitted means "no recalls here", which
   * is what every caller that predates recalls in options wants.
   */
  knownAliasIds: ReadonlySet<string> = new Set(),
): string {
  const escaped = formula.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Only names that exist become chips. A formula can hold literal braces — FormulaField leaves an
  // unknown `{zzz}` as text precisely so a chip can never point at a variable that is not there —
  // and converting blindly here would smuggle one in through the equations toggle, by a route that
  // never touched the variables menu.
  const withChips = escaped.replace(/\{([^}]+)\}/g, (match, name: string) =>
    knownNames.has(name) ? `<span data-variable-chip="${name}">${name}</span>` : match);
  const withRecalls = withChips.replace(/\[(ara_[0-9a-zA-Z-]+)\]/g, (match, id: string) =>
    (knownAliasIds.has(id) ? `<span data-answer-ref-alias="${id}"></span>` : match));
  return `<p>${withRecalls}</p>`;
}

/**
 * Chip HTML for a label that may be rich text OR a formula string — for surfaces that take an HTML
 * string rather than a node, chiefly the trigger pickers' `preview`.
 *
 * Both representations end up as real chips, which is the point. `bracketizeVariableChips`, what
 * these previews used, turns a chip span into `{name}` TEXT and leaves a formula's own braces
 * untouched — so either way the menu showed literal braces. A variable is a chip or a value.
 *
 * A formula takes the long way round via `formulaToRichText`, which rewrites `{name}` into the
 * editor's own chip span and only for names that actually exist, so an unknown `{zzz}` stays text
 * rather than being smuggled in as a chip pointing at nothing.
 */
export function labelChipHtml(raw: string, opts?: { asFormula?: boolean; knownNames?: Set<string> }): string {
  if (!raw) return raw;
  return renderChipsForDisplay(opts?.asFormula ? formulaToRichText(raw, opts.knownNames ?? new Set()) : raw);
}

/**
 * Replaces variable chip spans with plain text values for preview mode.
 *
 * A name with no value in `values` falls back to a DISPLAY CHIP rather than being left alone. It
 * used to be left alone, and that was the quietest bug in the variable system: a chip span's own
 * serialized text is the bare name — the extension omits the braces deliberately — so every caller
 * that then injected the result as HTML rendered `test` instead of `{test}`, a value, or anything
 * marked as a variable at all. Reaching this with an empty `values` is ordinary, not exceptional:
 * a test surface shows chips before the teacher has rolled anything.
 */
export function processVariableChipHTML(
  html: string,
  values: Record<string, string>,
  scope?: ChipScope,
): string {
  if (!html || !htmlHasVariableChips(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-variable-chip]').forEach(span => {
    const name = span.getAttribute('data-variable-chip') ?? '';
    if (!name) return;
    if (values[name] !== undefined) {
      span.replaceWith(document.createTextNode(values[name]));
      return;
    }
    /* A RESOLVED chip is a value and carries no mark — scope is a fact about the NAME, and
       once the name is gone there is nothing for the dot to qualify. Only the fallback chip,
       which is still showing a name, can be local. */
    span.replaceWith(displayChip(name, scope));
  });
  return div.innerHTML;
}

// ── Context ────────────────────────────────────────────────────────────────

/**
 * How a block's prose is set — the question on all eleven input blocks, and the body of the text
 * block. ONE string, because they are required to read the same.
 *
 * The input blocks used to add `font-medium` to this and the text block did not, so the same
 * sentence was weight 500 as a question and weight 400 as prose. That was never a decision anyone
 * made in one place: `font-medium` predates the shared frame below (MCQ already carried it), and
 * the frame simply inherited it while four blocks went on hand-rolling the same class string. It is
 * now the text block's treatment, exported so a fifteenth call site cannot quietly disagree.
 */
export const BLOCK_TEXT_CLASS = 'break-words text-sm text-ws-text ws-rich-text';

/**
 * The "no question yet" note. Teacher scaffolding, so it must never reach a student surface — see
 * `BlockQuestion`, which owns that decision now.
 */
export function EmptyQuestionNote() {
  const { t } = useTranslation();
  return <p className='mb-3 text-sm italic text-ws-muted'>{t('blocks.emptyState.noQuestion')}</p>;
}

/**
 * A block's question — one implementation for all eleven.
 *
 * There were thirteen, and the differences between them were all accidental. Chips-versus-resolved,
 * the one difference that IS real, was never encoded at the call site to begin with: `PreviewHtml`
 * reads `BlockPreviewProvider` from context, so the same call renders `{n}` as a pill on the canvas
 * card and as a rolled number in Preview. That is why thirteen copies could be byte-identical apart
 * from a margin and still be correct.
 *
 * What the copies genuinely disagreed on was the EMPTY state, and only in spelling: five blocks put
 * the note in their unselected branch, Numerical and Slider render the question above that branch
 * and so gate it on `!isSelected`, and the three sensors keep it outside their answer display
 * entirely. All three say the same thing — teacher scaffolding never reaches a live test surface —
 * so it is one prop here.
 *
 * `renderer` exists for FITB, whose question carries inline blank chips. That is not a fourth
 * surface; it is the same axis as variable chips (how authored HTML becomes nodes), which is why
 * `FitbQuestionPreview` is `PreviewHtml` plus one transform and reads the very same context.
 */
export function BlockQuestion({ html, spaced = false, showEmptyNote = false, renderer }: {
  html?: string;
  /** The collapsed cards separate the question from the answer with a margin; the surfaces that
   *  sit in a `gap-3` column must NOT add one. */
  spaced?: boolean;
  /** Display state only. A selected card is the live test surface, and a student must never be
   *  shown authoring scaffolding. */
  showEmptyNote?: boolean;
  renderer?: (p: { html: string; className: string }) => ReactNode;
}) {
  if (!html) return showEmptyNote ? <EmptyQuestionNote /> : null;
  const className = spaced ? `mb-3 ${BLOCK_TEXT_CLASS}` : BLOCK_TEXT_CLASS;
  return <>{renderer ? renderer({ html, className }) : <PreviewHtml html={html} className={className} />}</>;
}

/**
 * The roll a subtree resolves variable chips against, or `null` for a DISPLAY surface.
 *
 * `null` and `{}` are NOT interchangeable and the difference is easy to get wrong: `PreviewHtml`
 * switches on `preview !== null`, so an empty object still counts as "resolve me" and runs
 * `processVariableChipHTML` with nothing to substitute — which strips the chip markup and leaves
 * bare text where a chip should be. A display surface must pass `null`, never `{}`.
 */
const BlockPreviewContext = createContext<Record<string, string> | null>(null);

export const BlockPreviewProvider = BlockPreviewContext.Provider;

export function useBlockPreview(): Record<string, string> | null {
  return useContext(BlockPreviewContext);
}

/**
 * WHICH NAMES BELONG TO THIS TASK ALONE — what the `·` on a chip means.
 *
 * Scope is visible in the PANEL ("Experience Variables", *"Shared by every task in this
 * experience"*) and invisible on a chip in prose. The owner's case is not only copying:
 * *"you are casually flipping from task 1 to task 2 and both use `{n}` but they're local and
 * don't mean the same thing, but both sit next to `{star}` which is global."* Two
 * identical-looking chips meaning different things is the collision this codebase keeps
 * finding.
 *
 * THE LOCAL ONE IS MARKED, which started the other way round. `·` is already this app's
 * containment connector — `Pick a planet · Colours · Blank 1` — so a LEADING dot reads as
 * "[something] · n", a dangling path whose implied parent is the task. That says local.
 * Marking the global with it would fight a meaning teachers already read.
 *
 * DERIVED, NEVER STORED. A scope baked into the chip at insert time goes stale at exactly the
 * moment a teacher follows the advice to promote the variable.
 *
 * EMPTY IS THE SAFE DEFAULT. A surface outside the provider marks nothing, which is the state
 * every test and every non-task surface was already in. The cost is that a site which forgets
 * to pass it is silently unmarked rather than loudly wrong, so `invariants.test.ts` counts the
 * call sites and insists each one is scope-aware.
 */
const LocalVariableContext = createContext<ReadonlySet<string>>(new Set());

export const LocalVariableProvider = LocalVariableContext.Provider;

export function useLocalVariableNames(): ReadonlySet<string> {
  return useContext(LocalVariableContext);
}

/**
 * The scope a display chip is rendered against — the ONLY way a `ChipScope` should be built.
 *
 * Two facts that must travel together, from the two places that hold them: the names from the
 * page's provider, the word from i18n. Composed here so no call site assembles its own and
 * gets one of them from somewhere else.
 */
export function useChipScope(): ChipScope {
  const localNames = useLocalVariableNames();
  const { t } = useTranslation();
  const localLabel = t('task.settings.variables.taskVariableChip');
  return useMemo(() => ({ localNames, localLabel }), [localNames, localLabel]);
}

// ── PreviewHtml ────────────────────────────────────────────────────────────
// Drop-in replacement for dangerouslySetInnerHTML divs/spans inside a block's
// collapsed children. Automatically substitutes chips when inside a preview context.

export function PreviewHtml({
  html,
  className,
  as: Tag = 'div',
  title,
}: {
  html: string;
  className?: string;
  /** `figcaption` is here because it takes FLOW content: tiptap emits <p>, which a <span> may not
   *  legally contain, so the image caption renders into the figcaption itself rather than a
   *  wrapper inside it. */
  as?: 'div' | 'span' | 'figcaption';
  /** Native hover tooltip — e.g. surfacing the full text of a visually-truncated label so it's
   * never truly inaccessible to a sighted mouse user, just not shown at full length by default. */
  title?: string;
}) {
  const preview = useBlockPreview();
  /*
   * Answer references resolve FIRST, and read the same three ways a variable chip does.
   *
   * `preview` is the signal for both: `null` is the authoring canvas, where a reference stays
   * a chip, and a record is a test surface, where it resolves. In a run it resolves to the
   * student's own answer; under test, with no student, to a sample of what a correct answer
   * would be — which is what lets a teacher read the sentence they are writing.
   */
  const withAnswers = useAnswerRefHtml(preview)(html);
  const scope = useChipScope();
  // `remapInkColors` then `processReactionHTML`, both for the same reason: a stored palette hex
  // and a stored reaction DRAWING are each a snapshot of how things were rendered on the day the
  // content was saved, and each is re-derived here so a correction reaches what is already
  // written. Without the second one the card and Preview drew the same reaction differently.
  const rendered = processReactionHTML(remapInkColors(preview !== null
    ? processVariableChipHTML(withAnswers, preview, scope)
    : renderChipsForDisplay(withAnswers, scope)));
  // `data-authored` marks content a TEACHER wrote, for Preview's accessibility reports. They must
  // flag only what a teacher can actually fix: a contrast finding against Agavi's own chrome is
  // unfixable, and two or three of those and nobody opens the panel again. Missing a site yields a
  // false NEGATIVE (an unchecked string), which is the safe direction — see `isAuthoredContent`.
  return <Tag data-authored='' className={className} title={title} dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(rendered) }} />;
}

// ── AnswerDisplayFrame ─────────────────────────────────────────────────────
// The frame every block's `*AnswerDisplay` puts around its widget: one roll provided to the whole
// subtree, a column, and the question above it. Written out five times before this, and the five
// had drifted apart in both of the things it does:
//
//   - MCQ and Scale printed an authoring placeholder ("No question yet") where the question
//     belongs — the exact thing Dropdown's, Keyword's and Ordering's own comments say must never
//     reach a student, since this component IS the student surface (Preview, the canvas test
//     widget and the phone Test tab all render it). A blank question is reported by the card's
//     status bar and the issues panel, which is where authoring scaffolding belongs. Settled here
//     in the direction the majority already documented.
//   - Ordering rendered the question with a raw `dangerouslySetInnerHTML` div rather than
//     PreviewHtml. Not a live bug — its three call sites all pass an already-resolved question, so
//     there was never a chip left to process (verified live) — but it meant one of the five
//     silently depended on every caller pre-resolving, with nothing saying so. Going through
//     PreviewHtml is a no-op on resolved HTML and correct on raw.

/**
 * THE COLUMN AN ANSWER CONTROL SITS IN, wherever it is shown.
 *
 * `AnswerDisplayFrame` owns it for the surfaces a student can act on — Preview and the
 * editor's live test card. The COLLAPSED card does not route through that frame: it draws a
 * read-only summary of the same options, hand-rolled per block, and so was missing the column
 * entirely. The two sat side by side on one canvas, one capped and centred and one running the
 * full width of the card, which read as the block changing shape when you selected it.
 *
 * Exported as the class rather than as a component because a summary brings its own flex
 * direction and gap. What has to be shared is the CAP and the centring, and nothing else.
 */
export const ANSWER_COLUMN_CLASS = 'mx-auto w-full md:max-w-md';

export function AnswerDisplayFrame({ question, rolledValues, scenario, wide, children }: {
  /** Raw question HTML. Rendered only when non-empty — never replaced by a placeholder. */
  question?: string;
  /** Drop the centred column cap, for an answer that is itself laid out in columns. */
  wide?: boolean;
  /**
   * THE SCENARIO THE QUESTION IS ASKED ABOUT, above it, for a follower under test.
   *
   * A follower's stem lives in another block, which on a long task is a screen away and on a
   * phone is not on screen at all — so testing one meant reading the question, scrolling to
   * find the leader, and scrolling back. Put it where the student meets it and the round trip
   * disappears.
   *
   * A `ReactNode` rather than HTML on purpose: this file must not import the scenario module,
   * which imports back from here. Callers pass `<ScenarioContext>`, so the prose keeps one
   * owner and this frame keeps knowing nothing about scenarios.
   *
   * PREVIEW PASSES NOTHING. There the leader block is itself on the page and renders its own
   * drawn variant, so repeating it inside the follower would print the stem twice.
   */
  scenario?: ReactNode;
  /** The roll for this subtree. `null` means display mode (chips stay chips); a record means
   * resolve against it. Passing `{}` is the long-standing trap — see BlockPreviewContext's own
   * doc comment — so it is spelled out in the type rather than left to each caller to remember. */
  rolledValues: Record<string, string> | null;
  children: ReactNode;
}) {
  return (
    <BlockPreviewProvider value={rolledValues}>
      <div className='flex flex-col gap-3'>
        {/* INSIDE the provider, which is the whole point of putting it here rather than beside
            the frame: under test the scenario has to read as the student will read it, with its
            variables resolved. Outside, it rendered the same chips the editor shows, which is
            exactly the thing that made it unhelpful to test against. */}
        {/* The gap under it is the STIMULUS's own — see `ScenarioStimulus`. Putting it here
            covered only the blocks that route through this frame, and Numerical and Slider do
            not. */}
        {scenario}
        <BlockQuestion html={question} />
        {/*
         * THE ANSWER CONTROL IS CENTRED, on every block.
         *
         * It was centred on Dropdown and Keyword and left on MCQ, Numerical, Slider and Scale
         * — not a design, just whichever a block happened to get. There is no setting for it:
         * deriving one from the question's alignment cannot work, because `TextAlign` is
         * configured per PARAGRAPH and a question has no single alignment to read.
         *
         * CENTRING NEEDS A CAP, which is the part the first attempt got wrong. It wrapped the
         * children in a centred column and then forced them to full width, and a full-width
         * control is indistinguishable from a left-aligned one — MCQ's options looked exactly
         * as they had. So the column carries the cap, and a control that wants a narrower one
         * (Dropdown's and Keyword's `md:max-w-xs`) still sets its own inside it.
         *
         * `w-full` below `md`, so a phone keeps edge-to-edge controls and only the roomy case
         * centres. `items-stretch`, so a control still fills the column it has been given
         * rather than shrinking to its text.
         */}
        {/* `wide` DROPS THE CAP. The column exists so a stack of options does not sprawl across a
            wide card — but a block whose answer is itself laid out in COLUMNS is the one shape the
            cap works against: a horizontal sort had its source and every bin squeezed into 448px
            and centred, so even two bins were too narrow to read. The exception is the layout, not
            the block, which is why it is a prop and not a block list. */}
        <div className={`${wide ? 'w-full' : ANSWER_COLUMN_CLASS} flex flex-col items-stretch gap-3`}>
          {children}
        </div>
      </div>
    </BlockPreviewProvider>
  );
}
